/*
	This just provides a simplified interface for reading and editing query params.

	It returns an object with two functions, `getParam` and `setParam`. as well as a
	raw `params` URLSearchParams object as an escape hatch if you need to do anything more complicated.

	`getParam`
		- Takes a key and an optional value.
			- If `value` is not provided, it will just return the value of the query param for the key.
			- If `value` is provided, it will return true if the value matches the query param for the given key.
				- This papers over the URLSearchParams.has() method, which takes an optional value as well,
				  but it has poor browser support.

	`setParam`
		- Takes a key, value, and an optional `replaceLocation` flag.
			- If `replaceLocation` is true, it will dispatch an action to replace (not push) the location
			with the updated query param.
			- If `replaceLocation` is false, it will just update the params object, and you'll have to
			do something with it manually.
*/

import { useDispatch } from "react-redux";
import { useLocation } from "react-router-dom";
import { replace } from "connected-react-router";

export default function useQueryParams(){
	const dispatch = useDispatch();
	const location = useLocation();
	const params = new URLSearchParams(location.search);

	function setParam(key, value, replaceLocation = false){
		params.set(key, value);

		if(replaceLocation){
			const newUrl = `${location.pathname}?${params.toString()}`;
			dispatch(replace(newUrl));
		}
	}

	return {
		params,
		getParam(key, value){
			if(value !== undefined){
				return params.get(key) === value;
			}
			return params.get(key);
		},
		setParam,
	};
}

export function useCreativeIdsFromQuery() {
	const { getParam } = useQueryParams();
	const creativeIdsParam = getParam('creativeIds');

	if (!creativeIdsParam) {
		return [];
	}

	return creativeIdsParam.split(',')
		.map(id => parseInt(id.trim()))
		.filter(id => !isNaN(id));
}

export function useCreativeNameFromQuery() {
	const { getParam } = useQueryParams();
	const creativeName = getParam('creativeName');

	return creativeName ? decodeURIComponent(creativeName) : '';
}
